import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Button,Card,CardTitle } from "reactstrap";
import { connect } from "react-redux";
import LoadingOverlay from "react-loading-overlay";
import Loader from "react-loaders";
import BackButton from "../../../../designUtils/Logo/BackButton.svg";
import FetchAmazonNotificationLogsService from "../../../../Service/DashboardServices/AmazonNotificationsServices/FetchAmazonNotificationLogsService";
import GridPagination from "../../../Components/Pagination/GridPagination";
import CustomModal from "../../../Components/Modals/CustomModal";
import ReactTable from "react-table";
import NotificationPayloadInspector from "./Components/NotificationPayloadInspector";
import NotificationDetailsDrawer from "./Components/NotificationDetailsDrawer";
import {DEFAULT_NOTIFICATION_GUIDE,getClassification,mapApiNotification,getPrimaryId,payloadSnippet,
    severityBadgeClass,statusBadgeClass,entityBadgeClass,isWithinDateFilter} from "./Components/notificationMonitorUtils";


function AmazonNotificationsDashboard(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [notificationData, setNotificationData] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showPayloadPopup, setShowPayloadPopup] = useState(false);
    const [selectedPayload, setSelectedPayload] = useState([]);
    const [selectedNotificationType, setSelectedNotificationType] = useState("");
    const [guide, setGuide] = useState(DEFAULT_NOTIFICATION_GUIDE);
    const [showHelp, setShowHelp] = useState(false);
    const [selectedStoreName, setSelectedStoreName] = useState("");
    const [selectedItemKey, setSelectedItemKey] = useState(null);
    const [drawerItem, setDrawerItem] = useState(null);
    const [statusOverrides, setStatusOverrides] = useState({});
    const [filterSeverity, setFilterSeverity] = useState("");
    const [filterNeedsAttention, setFilterNeedsAttention] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const pageSize = 2000;




    /* =====================================================
       Fetch Notifications
       ===================================================== */

    useEffect(() => {
        fetchAmazonNotifications(0);
    }, [props.storeIntegerID]);


    useEffect(() => {
        if (props.storeName) {
            setSelectedStoreName(props.storeName);
        }
    }, [props.storeName]);


    const fetchAmazonNotifications = (
        page = 0,
        searchText = search
    ) => {

        setIsLoading(true);

        FetchAmazonNotificationLogsService(props.token, {
            page: page + 1,
            pageSize,
            search: searchText,
            integerID: props.storeIntegerID
        }).then((response) => {

            if (response && response.status) {

                setNotificationData(
                    response.notificationList || []
                );

                setTotalRecords(
                    response.totalRecords
                );
            }

            setIsLoading(false);
        });
    };

    const handleGuideChange = (index, field, value) => {
        setGuide((prev) =>
            prev.map((item, i) =>
                i === index
                    ? {
                          ...item,
                          [field]: value
                      }
                    : item
            )
        );
    };


    /* =====================================================
       Map Notifications
       ===================================================== */

    const mappedNotifications = useMemo(() => {

        return (notificationData || []).map(
            (row, index) => {

                const mapped = mapApiNotification(
                    row,
                    index
                );

                if (statusOverrides[mapped.id]) {
                    mapped.status =
                        statusOverrides[mapped.id];
                }

                return mapped;
            }
        );

    }, [
        notificationData,
        statusOverrides
    ]);


    /* =====================================================
       Store Names
       ===================================================== */

    const storeNames = useMemo(() => {

        const names = [];

        mappedNotifications.forEach((n) => {

            if (
                n.store &&
                names.indexOf(n.store) === -1
            ) {
                names.push(n.store);
            }

        });

        if (
            props.storeName &&
            names.indexOf(props.storeName) === -1
        ) {
            names.unshift(props.storeName);
        }

        return names;

    }, [
        mappedNotifications,
        props.storeName
    ]);


    const activeStore =
        selectedStoreName ||
        storeNames[0] ||
        props.storeName ||
        "";


    /* =====================================================
       Store Notifications
       ===================================================== */

    const storeNotifs = useMemo(
        () =>
            mappedNotifications.filter(
                (n) =>
                    !activeStore ||
                    n.store === activeStore ||
                    !n.store
            ),
        [
            mappedNotifications,
            activeStore
        ]
    );


    /* =====================================================
       Item Summary
       ===================================================== */

    const itemSummary = useMemo(() => {

        const itemMap = {};

        storeNotifs.forEach((n) => {

            const primaryId = getPrimaryId(n);

            if (!primaryId) {
                return;
            }

            if (!itemMap[primaryId]) {

                itemMap[primaryId] = {
                    displayId: primaryId,
                    entityType: n.identifierType,
                    asin: n.asin || "-",
                    sku: n.sku || "-",
                    sellerId: n.sellerId || "-",

                    total: 0,
                    critical: 0,
                    high: 0,
                    medium: 0,
                    low: 0,

                    types: new Set()
                };
            }

            itemMap[primaryId].total += 1;

            itemMap[primaryId].types.add(
                n.type
            );

            const classification =
                getClassification(
                    n.type,
                    guide
                );

            if (
                classification.severity ===
                "CRITICAL"
            ) {
                itemMap[primaryId].critical += 1;
            }
            else if (
                classification.severity ===
                "HIGH"
            ) {
                itemMap[primaryId].high += 1;
            }
            else if (
                classification.severity ===
                "MEDIUM"
            ) {
                itemMap[primaryId].medium += 1;
            }
            else {
                itemMap[primaryId].low += 1;
            }
        });

        return Object.values(itemMap);

    }, [
        storeNotifs,
        guide
    ]);


    /* =====================================================
       Selected Item
       ===================================================== */

    const effectiveItemKey = useMemo(() => {

        if (
            selectedItemKey &&
            itemSummary.some(
                (item) =>
                    item.displayId ===
                    selectedItemKey
            )
        ) {
            return selectedItemKey;
        }

        return itemSummary[0]
            ? itemSummary[0].displayId
            : null;

    }, [
        selectedItemKey,
        itemSummary
    ]);


    /* =====================================================
       Notification Types
       ===================================================== */

    const uniqueTypes = useMemo(() => {

        const types = [];

        mappedNotifications.forEach((n) => {

            if (
                n.type &&
                types.indexOf(n.type) === -1
            ) {
                types.push(n.type);
            }

        });

        return types;

    }, [
        mappedNotifications
    ]);


    /* =====================================================
       Filtered Issues
       ===================================================== */

    const filteredIssues = useMemo(() => {

        const q = search.toLowerCase();

        return storeNotifs.filter((n) => {

            const primaryId =
                getPrimaryId(n);

            if (
                effectiveItemKey &&
                primaryId !== effectiveItemKey
            ) {
                return false;
            }

            const classification =
                getClassification(
                    n.type,
                    guide
                );

            if (
                q &&
                !(n.asin || "")
                    .toLowerCase()
                    .includes(q) &&
                !(n.type || "")
                    .toLowerCase()
                    .includes(q) &&
                !(n.sku || "")
                    .toLowerCase()
                    .includes(q) &&
                !(n.sellerId || "")
                    .toLowerCase()
                    .includes(q)
            ) {
                return false;
            }

            if (
                filterSeverity &&
                classification.severity !==
                    filterSeverity
            ) {
                return false;
            }

            if (
                filterNeedsAttention &&
                classification.needsAttention !==
                    filterNeedsAttention
            ) {
                return false;
            }

            if (
                filterStatus &&
                n.status !== filterStatus
            ) {
                return false;
            }

            if (
                filterType &&
                n.type !== filterType
            ) {
                return false;
            }

            if (
                !isWithinDateFilter(
                    n.eventTimeRaw,
                    filterDate
                )
            ) {
                return false;
            }

            return true;
        });

    }, [
        storeNotifs,
        effectiveItemKey,
        search,
        guide,
        filterSeverity,
        filterNeedsAttention,
        filterStatus,
        filterType,
        filterDate
    ]);


    /* =====================================================
       KPI
       ===================================================== */

    const kpi = useMemo(() => {

        let criticalCount = 0;
        let highCount = 0;
        let needsAttentionCount = 0;

        storeNotifs.forEach((n) => {

            const c =
                getClassification(
                    n.type,
                    guide
                );

            if (
                c.severity === "CRITICAL"
            ) {
                criticalCount += 1;
            }

            if (
                c.severity === "HIGH"
            ) {
                highCount += 1;
            }

            if (
                c.needsAttention === "Yes"
            ) {
                needsAttentionCount += 1;
            }
        });

        return {
            total: storeNotifs.length,
            criticalCount,
            highCount,
            needsAttentionCount
        };

    }, [
        storeNotifs,
        guide
    ]);


    /* =====================================================
       Payload
       ===================================================== */

    const openPayload = (item) => {

        setSelectedPayload(
            item.payload
        );

        setSelectedNotificationType(
            item.type
        );

        setShowPayloadPopup(true);
    };


    /* =====================================================
       Update Status
       ===================================================== */

    const updateIssueStatus = (
        newStatus
    ) => {

        if (!drawerItem) {
            return;
        }

        setStatusOverrides(
            (prev) => ({
                ...prev,
                [drawerItem.id]:
                    newStatus
            })
        );

        setDrawerItem(null);
    };


    const guideColumns = [
        {
            Header: "Notification Type",
            accessor: "type",
            width: 180
        },
        {
            Header: "Category",
            accessor: "category",
            width: 165
        },
        {
            Header: "Severity",
            accessor: "severity",
            width: 140,
            Cell: ({ value, row }) => (
                <select
                    className="form-control form-control-sm"
                    value={value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            row.index,
                            "severity",
                            e.target.value
                        )
                    }
                >
                    <option value="CRITICAL">CRITICAL</option>
                    <option value="HIGH">HIGH</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="LOW">LOW</option>
                </select>
            )
        },
        {
            Header: "Business Impact",
            accessor: "businessImpact",
            width: 165,
            Cell: ({ value, row }) => (
                <select
                    className="form-control form-control-sm"
                    value={value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            row.index,
                            "businessImpact",
                            e.target.value
                        )
                    }
                >
                    <option value="Extremely High">Extremely High</option>
                    <option value="Very High">Very High</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                </select>
            )
        },
        {
            Header: "Attention",
            accessor: "needsAttention",
            width: 140,
            Cell: ({ value, row }) => (
                <select
                    className="form-control form-control-sm"
                    value={value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            row.index,
                            "needsAttention",
                            e.target.value
                        )
                    }
                >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                </select>
            )
        },
        {
            Header: "Recommended Action",
            accessor: "recommendedAction",
            minWidth: 260
        }
    ];

    return (

        <div className="anm-monitor mt-5">

<Row className="align-items-center mb-4 mt-2">

    <Col md={7} className="d-flex align-items-center">

        <button
            type="button"
            className="amazon-notification-back-button-header d-flex align-items-center"
            onClick={props.onBackHeader}
        >
            <img
                src={BackButton}
                alt="Back"
            />
        </button>

        <span className="amazon-notification-title ml-2">
            Amazon Notification Monitoring
        </span>

    </Col>

    <Col
        md={5}
        className="d-flex justify-content-end align-items-center"
    >
        <button
            type="button"
            className="bulk-creation-button"
            onClick={() => setShowHelp(true)}
        >
            Amazon Notification Settings
        </button>
    </Col>

</Row>

            {/* =================================================
                KPI CARDS
            ================================================= */}

            <Row className="mb-4">

                <Col md={3}>

                    <div className="anm-kpi-card total">

                        <div className="anm-kpi-title">
                            Total Issues (
                            {activeStore || "Store"}
                            )
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.total.toLocaleString()}
                        </div>

                    </div>

                </Col>


                <Col md={3}>

                    <div className="anm-kpi-card critical">

                        <div className="anm-kpi-title">
                            Critical
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.criticalCount}
                        </div>

                    </div>

                </Col>


                <Col md={3}>

                    <div className="anm-kpi-card high">

                        <div className="anm-kpi-title">
                            High
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.highCount}
                        </div>

                    </div>

                </Col>


                <Col md={3}>

                    <div className="anm-kpi-card needs-attention">

                        <div className="anm-kpi-title">
                            Needs Attention
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.needsAttentionCount}
                        </div>

                    </div>

                </Col>

            </Row>


            {/* =================================================
                STORE SELECTOR
            ================================================= */}

            <div className="anm-card">
                <Row className="anm-card-header align-items-center">
                    <Col md={8}>
                        <h3>
                            Amazon Store Selector
                        </h3>
                    </Col>


                    <Col md={4}>

                        <div className="text-right">

                            <select
                                className="form-control"
                                value={activeStore}
                                onChange={(e) => {

                                    setSelectedStoreName(
                                        e.target.value
                                    );

                                    setSelectedItemKey(
                                        null
                                    );

                                }}
                            >

                                {storeNames.length === 0 && (

                                    <option
                                        value={activeStore}
                                    >
                                        {activeStore ||
                                            "No store"}
                                    </option>

                                )}

                                {storeNames.map(
                                    (s) => (

                                        <option
                                            key={s}
                                            value={s}
                                        >
                                            {s}
                                        </option>

                                    )
                                )}

                            </select>

                        </div>

                    </Col>

                </Row>

            </div>


            {/* =================================================
                LOADING
            ================================================= */}

            <LoadingOverlay
                active={isLoading}
                spinner={
                    <Loader
                        active
                        type="ball-pulse"
                    />
                }
                text="Loading Amazon Notifications..."
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: "#f1f4f6",
                        opacity: 1
                    }),
                    content: (base) => ({
                        ...base,
                        color: "#000"
                    })
                }}
            >


                {/* =================================================
                    ITEM SUMMARY
                ================================================= */}

                <div className="anm-card">

                    <Row className="anm-card-header">

                        <Col md={8}>

                            <h3>
                                ASIN / SKU / Seller ID-Level
                                Issue Summary
                                {activeStore
                                    ? ` for ${activeStore}`
                                    : ""}
                            </h3>

                        </Col>

                    </Row>


                    <div className="anm-table-responsive">

                        <table>

                            <thead>

                                <tr>
                                    <th>Identifier Type</th>
                                    <th>ASIN</th>
                                    <th>SKU</th>
                                    <th>Seller ID</th>
                                    <th>Total Issues</th>
                                    <th>Critical</th>
                                    <th>High</th>
                                    <th>Medium</th>
                                    <th>Low</th>
                                    <th>Active Types</th>
                                    <th>Action</th>
                                </tr>

                            </thead>


                            <tbody>

                                {itemSummary.length === 0 ? (

                                    <tr>

                                        <td
                                            colSpan="11"
                                            className="anm-empty"
                                        >
                                            No items or seller
                                            accounts found for
                                            this store.
                                        </td>

                                    </tr>

                                ) : (

                                    itemSummary.map(
                                        (item) => {

                                            const isSelected =
                                                item.displayId ===
                                                effectiveItemKey;

                                            const typesString =
                                                Array.from(
                                                    item.types
                                                ).join(", ");

                                            return (

                                                <tr
                                                    key={
                                                        item.displayId
                                                    }
                                                    className={`anm-item-row${
                                                        isSelected
                                                            ? " selected"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        setSelectedItemKey(
                                                            item.displayId
                                                        )
                                                    }
                                                >

                                                    <td>

                                                        <span
                                                            className={`anm-badge ${entityBadgeClass(
                                                                item.entityType
                                                            )}`}
                                                        >
                                                            {
                                                                item.entityType
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        {item.asin !==
                                                        "-" ? (

                                                            <strong>
                                                                {
                                                                    item.asin
                                                                }
                                                            </strong>

                                                        ) : (

                                                            <span className="anm-muted">
                                                                -
                                                            </span>

                                                        )}

                                                    </td>


                                                    <td>

                                                        {item.sku !==
                                                        "-" ? (

                                                            <strong>
                                                                {
                                                                    item.sku
                                                                }
                                                            </strong>

                                                        ) : (

                                                            <span className="anm-muted">
                                                                -
                                                            </span>

                                                        )}

                                                    </td>


                                                    <td>

                                                        {item.sellerId !==
                                                        "-" ? (

                                                            <span className="anm-seller-id">
                                                                {
                                                                    item.sellerId
                                                                }
                                                            </span>

                                                        ) : (

                                                            <span className="anm-muted">
                                                                -
                                                            </span>

                                                        )}

                                                    </td>


                                                    <td>
                                                        <strong>
                                                            {
                                                                item.total
                                                            }
                                                        </strong>
                                                    </td>


                                                    <td>

                                                        <span className="anm-badge anm-badge-critical">
                                                            {
                                                                item.critical
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span className="anm-badge anm-badge-high">
                                                            {
                                                                item.high
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span className="anm-badge anm-badge-medium">
                                                            {
                                                                item.medium
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span className="anm-badge anm-badge-low">
                                                            {
                                                                item.low
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span
                                                            className="anm-types"
                                                            title={
                                                                typesString
                                                            }
                                                        >
                                                            {
                                                                typesString
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <button
                                                            type="button"
                                                            className="anm-action-btn"
                                                            onClick={(
                                                                e
                                                            ) => {

                                                                e.stopPropagation();

                                                                setSelectedItemKey(
                                                                    item.displayId
                                                                );

                                                            }}
                                                        >
                                                            Select
                                                        </button>

                                                    </td>

                                                </tr>

                                            );
                                        }
                                    )

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>


                {/* =================================================
                    NOTIFICATION ISSUES
                ================================================= */}

                <div className="anm-card">

                    <Row className="anm-card-header align-items-center">

                        <Col md={8}>

                            <h3>
                                Notification Issues
                            </h3>

                            <div className="anm-card-subtitle">
                                View notification logs filtered
                                by severity and attention rules
                                configured in the guide
                            </div>

                        </Col>


                        <Col md={4}>

                            <div className="d-flex justify-content-end">

                                <GridPagination
                                    pageSize={pageSize}
                                    totalRecords={
                                        totalRecords
                                    }
                                    currentPageProp={
                                        currentPage
                                    }
                                    onPageChange={({
                                        currentPage: page
                                    }) => {

                                        setCurrentPage(
                                            page
                                        );

                                        fetchAmazonNotifications(
                                            page
                                        );

                                    }}
                                />

                            </div>

                        </Col>

                    </Row>


                    {/* =================================================
                        FILTERS
                    ================================================= */}

                    <div className="anm-filter-toolbar">

                        <Row>

                            <Col md={4} className="mb-2">

                                <input
                                    type="text"
                                    className="w-100"
                                    placeholder="Search ASIN, SKU, Seller ID, notification type..."
                                    value={search}
                                    onChange={(e) => {

                                        const value =
                                            e.target.value;

                                        setSearch(value);
                                        setCurrentPage(0);

                                        fetchAmazonNotifications(
                                            0,
                                            value
                                        );

                                    }}
                                />

                            </Col>


                            <Col md={2} className="mb-2">

                                <select
                                    className="w-100"
                                    value={
                                        filterSeverity
                                    }
                                    onChange={(e) =>
                                        setFilterSeverity(
                                            e.target.value
                                        )
                                    }
                                >

                                    <option value="">
                                        Severity: All
                                    </option>

                                    <option value="CRITICAL">
                                        Critical
                                    </option>

                                    <option value="HIGH">
                                        High
                                    </option>

                                    <option value="MEDIUM">
                                        Medium
                                    </option>

                                    <option value="LOW">
                                        Low
                                    </option>

                                </select>

                            </Col>


                            <Col md={2} className="mb-2">

                                <select
                                    className="w-100"
                                    value={
                                        filterNeedsAttention
                                    }
                                    onChange={(e) =>
                                        setFilterNeedsAttention(
                                            e.target.value
                                        )
                                    }
                                >

                                    <option value="">
                                        Needs Attention: All
                                    </option>

                                    <option value="Yes">
                                        Yes
                                    </option>

                                    <option value="No">
                                        No
                                    </option>

                                </select>

                            </Col>


                            <Col md={2} className="mb-2">

                                <select
                                    className="w-100"
                                    value={filterStatus}
                                    onChange={(e) =>
                                        setFilterStatus(
                                            e.target.value
                                        )
                                    }
                                >

                                    <option value="">
                                        Status: All
                                    </option>

                                    <option value="NEW">
                                        New
                                    </option>

                                    <option value="OPEN">
                                        Open
                                    </option>

                                    <option value="IN PROGRESS">
                                        In Progress
                                    </option>

                                    <option value="RESOLVED">
                                        Resolved
                                    </option>

                                    <option value="INFORMATIONAL">
                                        Informational
                                    </option>

                                </select>

                            </Col>


                            <Col md={2} className="mb-2">

                                <select
                                    className="w-100"
                                    value={filterDate}
                                    onChange={(e) =>
                                        setFilterDate(
                                            e.target.value
                                        )
                                    }
                                >

                                    <option value="">
                                        Date: All
                                    </option>

                                    <option value="today">
                                        Today
                                    </option>

                                    <option value="7days">
                                        Last 7 Days
                                    </option>

                                </select>

                            </Col>


                            <Col md={3} className="mb-2">

                                <select
                                    className="w-100"
                                    value={filterType}
                                    onChange={(e) =>
                                        setFilterType(
                                            e.target.value
                                        )
                                    }
                                >

                                    <option value="">
                                        All Notification Types
                                    </option>

                                    {uniqueTypes.map(
                                        (type) => (

                                            <option
                                                key={type}
                                                value={type}
                                            >
                                                {type}
                                            </option>

                                        )
                                    )}

                                </select>

                            </Col>

                        </Row>

                    </div>


                    {/* =================================================
                        NOTIFICATION TABLE
                    ================================================= */}

                    <div className="anm-table-responsive">

                        <table>

                            <thead>

                                <tr>

                                    <th>
                                        Target Entity
                                        (ASIN / SKU / Seller ID)
                                    </th>

                                    <th>
                                        Notification Type
                                    </th>

                                    <th>
                                        Notification Payload
                                    </th>

                                    <th>
                                        Event Time
                                    </th>

                                    <th>
                                        Severity
                                    </th>

                                    <th>
                                        Business Impact
                                    </th>

                                    <th>
                                        Needs Attention
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Recommended Action
                                    </th>

                                    <th>
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {filteredIssues.length === 0 ? (

                                    <tr>

                                        <td
                                            colSpan="10"
                                            className="anm-empty"
                                        >
                                            No notifications match
                                            the filter criteria for
                                            this entity.
                                        </td>

                                    </tr>

                                ) : (

                                    filteredIssues.map(
                                        (item) => {

                                            const classification =
                                                getClassification(
                                                    item.type,
                                                    guide
                                                );

                                            let targetHtml =
                                                null;


                                            if (
                                                item.sellerId &&
                                                String(
                                                    item.sellerId
                                                ).trim() !== ""
                                            ) {

                                                targetHtml = (

                                                    <span>

                                                        <span className="anm-badge anm-badge-critical">
                                                            Seller ID
                                                        </span>

                                                        <br />

                                                        <strong className="anm-seller-id">
                                                            {
                                                                item.sellerId
                                                            }
                                                        </strong>

                                                    </span>

                                                );

                                            }
                                            else if (
                                                item.asin
                                            ) {

                                                targetHtml = (

                                                    <span>

                                                        <strong>
                                                            {
                                                                item.asin
                                                            }
                                                        </strong>

                                                        <br />

                                                        <span className="anm-small-muted">
                                                            SKU:{" "}
                                                            {
                                                                item.sku ||
                                                                "-"
                                                            }
                                                        </span>

                                                    </span>

                                                );

                                            }
                                            else {

                                                targetHtml = (

                                                    <span>

                                                        <span className="anm-no-asin">
                                                            No ASIN
                                                        </span>

                                                        <br />

                                                        <strong className="anm-sku">
                                                            SKU:{" "}
                                                            {
                                                                item.sku ||
                                                                "-"
                                                            }
                                                        </strong>

                                                    </span>

                                                );
                                            }


                                            return (

                                                <tr
                                                    key={item.id}
                                                >

                                                    <td>
                                                        {
                                                            targetHtml
                                                        }
                                                    </td>


                                                    <td>

                                                        <span className="anm-notification-type">
                                                            {
                                                                item.type
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span className="anm-payload-snippet">
                                                            {
                                                                payloadSnippet(
                                                                    item.payload
                                                                )
                                                            }
                                                        </span>

                                                        <button
                                                            type="button"
                                                            className="anm-action-btn"
                                                            onClick={() =>
                                                                openPayload(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            View Payload
                                                        </button>

                                                    </td>


                                                    <td>

                                                        <span className="anm-nowrap">
                                                            {
                                                                item.eventTime
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span
                                                            className={`anm-badge ${severityBadgeClass(
                                                                classification.severity
                                                            )}`}
                                                        >
                                                            {
                                                                classification.severity
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span className="anm-business-impact">
                                                            {
                                                                classification.businessImpact
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span
                                                            className={`anm-badge ${
                                                                classification.needsAttention ===
                                                                "Yes"
                                                                    ? "anm-badge-attention-yes"
                                                                    : "anm-badge-attention-no"
                                                            }`}
                                                        >
                                                            {
                                                                classification.needsAttention
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span
                                                            className={`anm-badge ${statusBadgeClass(
                                                                item.status
                                                            )}`}
                                                        >
                                                            {
                                                                item.status
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <span
                                                            className="anm-recommended-text"
                                                            title={
                                                                classification.recommendedAction
                                                            }
                                                        >
                                                            {
                                                                classification.recommendedAction
                                                            }
                                                        </span>

                                                    </td>


                                                    <td>

                                                        <button
                                                            type="button"
                                                            className="anm-action-btn"
                                                            onClick={() =>
                                                                setDrawerItem(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <i className="far fa-eye" />
                                                            {" "}
                                                            View
                                                        </button>

                                                    </td>

                                                </tr>

                                            );
                                        }
                                    )

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

            </LoadingOverlay>


            {/* =================================================
                DRAWER
            ================================================= */}

            {drawerItem && (

                <NotificationDetailsDrawer
                    item={drawerItem}
                    guide={guide}
                    onClose={() =>
                        setDrawerItem(null)
                    }
                    onUpdateStatus={
                        updateIssueStatus
                    }
                />

            )}


            {/* =================================================
                PAYLOAD POPUP
            ================================================= */}

            <NotificationPayloadInspector
                isOpen={
                    showPayloadPopup
                }
                toggle={() =>
                    setShowPayloadPopup(false)
                }
                selectedPayload={
                    selectedPayload
                }
                notificationType={
                    selectedNotificationType
                }
            />


            {/* =================================================
                GUIDE MODAL
            ================================================= */}

<CustomModal
    header="Amazon Notification Settings"
    isOpen={showHelp}
    toggle={() => setShowHelp(false)}
    size="xl"
    className="curve-popup"
>
<Row>
    <Col md={12}>
        <ReactTable
            data={guide || []}
            columns={guideColumns}
            className="-fixed -highlight -striped mt-2"
            getTheadProps={() => ({
                style: {
                    overflowY: "hidden",
                    background: '#B1FA63',
                        color: '#243837',
                }
            })}
            defaultPageSize={10}
            noDataText="No Data Found."
        />
    </Col>
</Row>

</CustomModal>



        </div>
    );
}


/* =========================================================
   Redux
   ========================================================= */

const mapStateToProps = (state) => ({
    token: state.loggedUser.token
});


export default connect(
    mapStateToProps
)(AmazonNotificationsDashboard);